import axios from "axios";
import Pusher from "pusher-js";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

// Pusher Configuration
export const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY;
export const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER;

// Shared Pusher Instance
export const pusher = new Pusher(PUSHER_KEY, {
  cluster: PUSHER_CLUSTER,
  forceTLS: true,
});

pusher.connection.bind("state_change", (states) => {
  console.log("🔌 Pusher Connection State:", states.current);
});

// Helper for debugging
const originalSubscribe = pusher.subscribe.bind(pusher);
pusher.subscribe = (channelName) => {
  console.log(`📡 Pusher: Subscribing to channel: ${channelName}`);
  return originalSubscribe(channelName);
};

pusher.connection.bind("error", (err) => {
  console.error("❌ Pusher Connection Error:", err);
});

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gutful_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const sendOTP = (email) => api.post("/auth/send-otp", { email });
export const verifyOTP = (email, otp) =>
  api.post("/auth/verify-otp", { email, otp });

// Cache configuration
const CACHE_PREFIX = "gutful_cache_";
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch (error) {
    return null;
  }
};

const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (error) {
    console.warn("Failed to save to cache:", error);
  }
};

// export const getMenuItems = (brand) =>
//   api.get(`/menu${brand ? `?brandType=${brand}` : ""}`);
// export const getMenuItem = (id) => api.get(`/menu/${id}`);
// export const getAddons = (brand) => api.get(`/addons${brand ? `?brand=${brand}` : ""}`);
// Menu APIs
export const getMenuItems = async (brand) => {
  const cacheKey = `${CACHE_PREFIX}menu_items_${brand || "all"}`;
  const cached = getCachedData(cacheKey);
  if (cached) return { data: cached };

  const response = await api.get(`/menu${brand ? `?brandType=${brand}` : ""}`);
  setCachedData(cacheKey, response.data);
  return response;
};

export const getMenuItem = async (id) => {
  const cacheKey = `${CACHE_PREFIX}menu_item_${id}`;
  const cached = getCachedData(cacheKey);
  if (cached) return { data: cached };

  const response = await api.get(`/menu/${id}`);
  setCachedData(cacheKey, response.data);
  return response;
};

export const getAddons = async (brand) => {
  const cacheKey = `${CACHE_PREFIX}addons_${brand || "all"}`;
  const cached = getCachedData(cacheKey);
  if (cached) return { data: cached };

  const response = await api.get(`/addons${brand ? `?brand=${brand}` : ""}`);
  setCachedData(cacheKey, response.data);
  return response;
};

export const getMyOrders = async () => {
  return api.get("/orders/my-orders");
};

export const createOrder = (orderData) => api.post("/orders", orderData);
export const cancelOrder = (orderId) =>
  api.post(`/orders/${orderId}/cancel`, {});
export const getOrder = (orderId) => api.get(`/orders/${orderId}`);
export const updateOrderPaymentStatus = (orderId, paymentStatus) =>
  api.patch(`/orders/${orderId}/payment-status`, { paymentStatus });

// Payment APIs
export const createPaymentOrder = (amount) =>
  api.post("/payments/order", { amount });
export const verifyPayment = (paymentData) =>
  api.post("/payments/verify", paymentData);

// Subscription APIs
export const getSubscriptions = async () => {
  const cacheKey = `${CACHE_PREFIX}subscriptions`;
  const cached = getCachedData(cacheKey);
  if (cached) return { data: cached };

  const response = await api.get("/subscriptions");
  setCachedData(cacheKey, response.data);
  return response;
};

export const createSubscription = (subscriptionData) =>
  api.post("/subscriptions", subscriptionData);
export const updateSubscription = (subscriptionData) =>
  api.post("/subscriptions", subscriptionData);
export const reorderSubscriptions = (orderedIds) =>
  api.put("/subscriptions/reorder", { orderedIds });
export const deleteSubscription = (subscriptionId) =>
  api.delete(`/subscriptions/${subscriptionId}`);

// Perks APIs
export const getPerks = async () => {
  const cacheKey = `${CACHE_PREFIX}perks`;
  const cached = getCachedData(cacheKey);
  if (cached) return { data: cached };

  const response = await api.get("/perks");
  setCachedData(cacheKey, response.data);
  return response;
};
export const savePerks = (perksData) =>
  api.post("/perks", { perks: perksData });
export const deletePerk = (perkId) => api.delete(`/perks/${perkId}`);

// Subscribed Members APIs
export const getSubscribedMembers = async () => {
  const response = await api.get("/subscribed-members");
  return response;
};
export const createSubscribedMember = async (data) => {
  const res = await api.post("/subscribed-members", data);
  clearCache("subscribed_members");
  return res;
};
export const updateSubscribedMember = async (id, data) => {
  const res = await api.put(`/subscribed-members/${id}`, data);
  clearCache("subscribed_members");
  return res;
};
export const deleteSubscribedMember = async (id) => {
  const res = await api.delete(`/subscribed-members/${id}`);
  clearCache("subscribed_members");
  return res;
};
export const skipMealSubscribedMember = async (id, data) => {
  const res = await api.put(`/subscribed-members/${id}/skip-meal`, data);
  clearCache("subscribed_members");
  return res;
};
export const deliverMealSubscribedMember = async (id, data) => {
  const res = await api.put(`/subscribed-members/${id}/deliver-meal`, data);
  clearCache("subscribed_members");
  return res;
};
export const setMealGivenSubscribedMember = async (id, data) => {
  const res = await api.put(`/subscribed-members/${id}/meal-given`, data);
  clearCache("subscribed_members");
  return res;
};
export const reduceDailyMeals = async (data) => {
  const res = await api.put(
    `/subscribed-members/action/reduce-daily-meals`,
    data
  );
  clearCache("subscribed_members");
  return res;
};
export const getMySubscription = () =>
  api.get("/subscribed-members/my-subscription");
export const customizeMySubscription = (data) =>
  api.put("/subscribed-members/my-subscription/customize", data);
export const skipMySubscription = (data) =>
  api.put("/subscribed-members/my-subscription/skip", data);

export const clearCache = (prefix = "") => {
  try {
    const fullPrefix = CACHE_PREFIX + prefix;
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(fullPrefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
};

export default api;
